import { Fragment, useState } from 'react';
import {
  can,
  faNum as fa,
  formatRial,
  type ReconAnomaly,
  type ReconUnpriced,
  type ReconciliationReport,
} from '@kia/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useApiResource } from '../lib/useApiResource';
import { Button, Card, Chip, ErrorRetry, Kpi, useConfirm, useToast } from '../ui';

/**
 * پل اطمینان — the reconciliation gate (SEPIDAR_INDEPENDENCE.md). The one screen a human trusts before the
 * shadow ledger can stand in for Sepidar: is the general ledger a faithful projection of operations?
 *
 *  Part A — completeness & integrity (the gate): unposted / pending-reversal / orphan counts + trial-balance
 *           balance. Green ⇒ the books mirror operations right now. The «همگام‌سازی» action posts what's
 *           missing and reverses what's stale (orphans stay for a human).
 *  Part B — the subledger worksheet: each control account beside the same figure recomputed independently
 *           from operations, opening balance split out, residual attributed. A human aid, not a hard gate.
 */

/**
 * BIDX-P4 — the NAMED unpriced-receipt line, rendered as a caption beneath EACH tie-out row these
 * unconfirmed prices actually move.
 *
 * THE SENTENCE IS THE POINT, AND IT IS NOT THE OBVIOUS ONE. Under BIDX-P4 the money plane still booked
 * every live receipt, so the copy said «در انتظار تأیید» and explicitly NOT «خارج از دفاتر» — a true
 * number under a false sentence would have been the worse failure, on the one screen a human trusts before
 * the shadow ledger stands in for Sepidar. BIDX-P6 FLIPPED THE PREDICATE AND THIS STRING CHANGED IN THAT
 * SAME COMMIT: the rials are now genuinely out of both rows, and the caption says so — plus what did NOT
 * leave (the goods, and the exposure itself, which is why this caption still exists at all).
 *
 * P4-REPAIR, THREE CORRECTIONS THE FIRST CUT GOT WRONG — ALL THREE STILL BINDING AFTER P6:
 *  1. NOT A SUBSET OF «از عملیات». The payables row's operations figure is `receiptsValue − clearedOut`
 *     (services/reconciliation.ts), and `clearedOut` is the book-wide SUM of cleared OUTBOUND cheques with
 *     NO receipt linkage. One unpriced receipt of 10,000 against 9,500 of cleared cheques renders a stated
 *     subset LARGER than the stated whole. The claim «هنوز داخل «از عملیات» بالا هست» was therefore
 *     arithmetically false in the ordinary case, on the worksheet this epic exists to make trustworthy.
 *     AFTER P6 THE HAZARD SURVIVES IN A NEW FORM: the rials are no longer inside the row at all, so the
 *     row can be NEGATIVE while the caption states a positive figure. The copy therefore still refuses to
 *     relate the two arithmetically — it does not invite adding them back either.
 *  2. INVENTORY MOVES BY THE SAME RIALS. services/inventory.ts now passes `valued: r.booked`, so
 *     materialsOnHand().totalValue — the inventory row's `operations` — omits these receipts. Captioning
 *     payables only would leave the identical exposure unnamed one row down, hiding inside inventory's own
 *     pre-written «اختلاف طبیعی است…» excuse. Inventory's clause makes no arithmetic claim either: the
 *     goods may be partly consumed, so the value withheld from that row is not `unpriced.rials`.
 *  3. THE ACTION NAMES WHO CAN PERFORM IT. «ثبت قیمت» renders under purchase.write (Admin/Editor/
 *     Purchasing) while this worksheet is accounting.read (Admin/Editor/Finance) — so the canonical
 *     reader, Finance, was told to press a control that does not render for them. Same fix, same wording
 *     as the prior art at data/repositories/workOrder.ts.
 */
function UnpricedCaption({
  group,
  unpriced,
  canPrice,
}: {
  group: 'payables' | 'inventory' | null;
  unpriced: ReconUnpriced;
  canPrice: boolean;
}) {
  if (!group || unpriced.count === 0) return null;
  return (
    <tr>
      <td colSpan={6} style={{ color: 'var(--ink-3)', fontSize: '.85em', lineHeight: 1.9 }}>
        {group === 'payables' ? (
          <>
            <b>{formatRial(unpriced.rials)}</b> ریال روی <b>{fa(unpriced.count)}</b> رسید خرید با
            «قیمت تأییدنشده» — قیمت پرداختی وارد نشده و از قیمت سفارش برآورد شده. این مبلغ از دفاتر
            کنار گذاشته شده است: نه سند حسابداری دارد، نه در عدد بالا شمرده شده
            {unpriced.oldestDays > 0 ? <> (قدیمی‌ترین: {fa(unpriced.oldestDays)} روز)</> : null}.
            یعنی «حساب‌های پرداختنی» تا این اندازه کمتر از واقع است. توجه: «از عملیات» بالا خالصِ
            رسیدهای خرید منهای چک‌های پرداخت‌شده است، پس این مبلغ نه زیرمجموعهٔ آن است و نه با جمع
            کردن به آن، عدد درستی به دست می‌آید — آن را با عدد بالا کم و زیاد نکنید.{' '}
          </>
        ) : (
          <>
            همین <b>{fa(unpriced.count)}</b> رسید با «قیمت تأییدنشده» (
            <b>{formatRial(unpriced.rials)}</b> ریال) از ارزش‌گذاری موجودی بالا هم بیرون است: کالا
            روی قفسه هست و در «موجودی مواد» شمرده می‌شود، ولی چون بهای تأییدشده ندارد ارزشی به این
            ردیف اضافه نمی‌کند — پس این ردیف تا تأیید قیمت، کمتر از واقع است. مقدارِ دقیقِ این کمبود
            برابرِ مبلغ بالا نیست، چون قیمت واقعی تا ثبت‌نشدن معلوم نیست و ممکن است بخشی از این کالا
            پیش‌تر مصرف شده باشد (دستور ساخت دیگر از کالای تأییدنشده مصرف نمی‌کند).{' '}
          </>
        )}
        {canPrice ? (
          <>با «ثبت قیمت» در «تاریخچهٔ خرید» تأیید کنید.</>
        ) : (
          <>
            تأیید با «ثبت قیمت» در «تاریخچهٔ خرید» انجام می‌شود؛ اگر دسترسی خرید ندارید، از واحد
            خرید بخواهید آن را ثبت کند.
          </>
        )}
      </td>
    </tr>
  );
}

function AnomalyList({
  title,
  items,
  tone,
}: {
  title: string;
  items: ReconAnomaly[];
  tone: string;
}) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <b style={{ color: tone }}>
        {title} ({fa(items.length)})
      </b>
      <table className="table" style={{ marginTop: 6 }}>
        <thead>
          <tr>
            <th style={{ width: '30%' }}>مرجع رویداد</th>
            <th>شرح</th>
            <th>مبلغ</th>
          </tr>
        </thead>
        <tbody>
          {items.slice(0, 50).map((a) => (
            <tr key={a.ref}>
              <td
                style={{ fontVariantNumeric: 'tabular-nums', direction: 'ltr', textAlign: 'right' }}
              >
                {a.ref}
              </td>
              <td style={{ color: 'var(--ink-2)' }}>{a.memo}</td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                {a.amount ? formatRial(a.amount) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 50 ? (
        <p style={{ color: 'var(--ink-3)', marginTop: 4 }}>… و {fa(items.length - 50)} مورد دیگر</p>
      ) : null}
    </div>
  );
}

export function ReconciliationPanel({ onSynced }: { onSynced?: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  // P4-REPAIR: this worksheet is accounting.read (Admin/Editor/Finance) but «ثبت قیمت» renders under
  // purchase.write (Admin/Editor/Purchasing) — the SAME permission the button itself is gated on in
  // BuyScreen. Finance is the canonical reader here and cannot press it, so the caption must name who can.
  const { user } = useAuth();
  const canPrice = user ? can(user.role, 'purchase.write') : false;
  const res = useApiResource<ReconciliationReport>('/api/accounting/reconciliation');
  const report = res.data;
  const [busy, setBusy] = useState(false);

  async function sync() {
    if (
      !(await confirm({
        title: 'همگام‌سازی دفترکل',
        message: 'اسناد جاافتاده ثبت و اسناد باطل‌شده در دفترکل برگشت داده می‌شوند. ادامه؟',
        danger: true,
      }))
    )
      return;
    setBusy(true);
    const r = await api<{
      posted: number;
      reversed: number;
      reinstated: number;
      orphan: number;
    }>('/api/accounting/sync', { method: 'POST' });
    setBusy(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در همگام‌سازی', 'error');
      return;
    }
    const s = r.body as { posted: number; reversed: number; reinstated: number; orphan: number };
    toast(
      `همگام‌سازی: ${fa(s.posted)} ثبت، ${fa(s.reversed)} برگشت` +
        `${s.reinstated ? `، ${fa(s.reinstated)} صدور مجدد` : ''}${s.orphan ? `، ${fa(s.orphan)} بی‌مرجع` : ''}`,
    );
    res.reload();
    onSynced?.();
  }

  const title = (
    <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
      پل اطمینان — تطبیق دفترکل با عملیات
      {report ? (
        <Chip tone={report.ok ? 'success' : 'warn'}>
          {report.ok ? 'دفترها با عملیات می‌خوانند ✓' : 'نیازمند همگام‌سازی/بررسی'}
        </Chip>
      ) : null}
    </span>
  );

  return (
    <Card title={title}>
      {res.status === 'error' ? (
        <ErrorRetry error={res.error} onRetry={res.reload} />
      ) : !report ? (
        <p style={{ color: 'var(--ink-3)' }}>در حال بارگذاری…</p>
      ) : (
        <>
          <p style={{ margin: '0 0 10px', color: 'var(--ink-2)', lineHeight: 1.9 }}>
            دفترها از رویدادهای عملیاتی (تحویل، وصول چک، رسید خرید، فاکتور، سپرده، وام، دستمزد)
            ساخته می‌شوند. این کارت نشان می‌دهد آیا هر رویداد به سند تبدیل شده و آیا حساب‌های کنترلی
            با زیرسیستم‌ها می‌خوانند — همان چیزی که در «اجرای موازی» با سپیدار باید اثبات شود.
          </p>

          {/* Part A — the gate */}
          <div className="kpi-grid" style={{ marginBottom: 12 }}>
            <Kpi
              label="اسناد جامانده"
              value={fa(report.unposted.length)}
              tone={report.unposted.length ? 'bad' : 'good'}
            />
            <Kpi
              label="در انتظار برگشت"
              value={fa(report.pendingReversal.length)}
              tone={report.pendingReversal.length ? 'bad' : 'good'}
            />
            <Kpi
              label="بی‌مرجع (orphan)"
              value={fa(report.orphan.length)}
              tone={report.orphan.length ? 'bad' : 'good'}
            />
            <Kpi
              label="تراز آزمایشی"
              value={report.balanced ? 'تراز ✓' : 'ناتراز ✗'}
              tone={report.balanced ? 'good' : 'bad'}
            />
          </div>

          <Button onClick={() => void sync()} disabled={busy}>
            {busy ? 'در حال همگام‌سازی…' : 'همگام‌سازی دفترها'}
          </Button>

          <AnomalyList
            title="جامانده — با همگام‌سازی ثبت می‌شوند"
            items={report.unposted}
            tone="var(--amber)"
          />
          <AnomalyList
            title="در انتظار برگشت — با همگام‌سازی برگشت می‌خورند"
            items={report.pendingReversal}
            tone="var(--amber)"
          />
          <AnomalyList
            title="بی‌مرجع — نیازمند بررسی انسانی"
            items={report.orphan}
            tone="var(--red)"
          />

          {/* Part B — the subledger worksheet */}
          <div style={{ marginTop: 16 }}>
            <b>تطبیق حساب‌های کنترلی با زیرسیستم‌ها</b>
            <table className="table" style={{ marginTop: 6 }}>
              <thead>
                <tr>
                  <th>حساب کنترلی</th>
                  <th>مانده دفترکل</th>
                  <th>مانده افتتاحیه</th>
                  <th>از عملیات</th>
                  <th>اختلاف</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {report.tieOuts.map((t) => (
                  <Fragment key={t.key}>
                    <tr>
                      <td>
                        {t.label}
                        <span style={{ color: 'var(--ink-3)', fontSize: '.85em' }}>
                          {' '}
                          ({t.accounts.join('،')})
                        </span>
                      </td>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatRial(t.gl)}</td>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {t.opening ? formatRial(t.opening) : '—'}
                      </td>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatRial(t.operations)}
                      </td>
                      <td
                        style={{
                          fontVariantNumeric: 'tabular-nums',
                          color: t.stale ? 'var(--ink-3)' : t.ok ? 'var(--green)' : 'var(--amber)',
                        }}
                      >
                        {formatRial(t.residual)}
                      </td>
                      <td>
                        {t.stale ? (
                          <Chip tone="warn">همگام‌سازی لازم</Chip>
                        ) : (
                          <Chip tone={t.ok ? 'success' : 'warn'}>
                            {t.ok ? 'می‌خواند ✓' : 'اختلاف'}
                          </Chip>
                        )}
                      </td>
                    </tr>
                    <UnpricedCaption
                      group={
                        t.key === 'payables' || t.key === 'inventory'
                          ? (t.key as 'payables' | 'inventory')
                          : null
                      }
                      unpriced={report.unpriced}
                      canPrice={canPrice}
                    />
                  </Fragment>
                ))}
              </tbody>
            </table>
            {report.tieOuts.some((t) => t.stale) ? (
              <p style={{ margin: '8px 0 0', color: 'var(--amber)', lineHeight: 1.9 }}>
                برخی ردیف‌ها تا «همگام‌سازی» معتبر نیستند — مانده‌شان به‌دلیل سند ثبت‌نشده/در انتظار
                برگشت موقتاً ناهم‌تراز نشان داده می‌شود. ابتدا همگام‌سازی کنید، سپس این کاربرگ را
                بخوانید.
              </p>
            ) : null}
            {report.tieOuts.some((t) => !t.ok && !t.stale) ? (
              <ul
                style={{
                  margin: '8px 0 0',
                  paddingInlineStart: 18,
                  color: 'var(--ink-3)',
                  lineHeight: 1.9,
                }}
              >
                {report.tieOuts
                  .filter((t) => !t.ok && !t.stale)
                  .map((t) => (
                    <li key={t.key}>
                      <b>{t.label}:</b> {t.note}
                    </li>
                  ))}
              </ul>
            ) : null}
          </div>
        </>
      )}
    </Card>
  );
}
